import mobx, { observable, runInAction, computed, action, autorunAsync } from 'mobx';
import TimelineEventResource from './timeline-event-resource';
import TimelineEventInstanceModel from './timeline-event-instance-model';
import moment from 'moment-business-days';
import Holidays from 'date-holidays';
import Cons from '../../../../isomorphic/cons';
import _ from 'lodash';
import TimelineRenderHelper from './timeline-render-helper';
import uuid from 'uuid';

export default class TimelineWorkflowStepStore {
    @observable isLoading = false;
    @observable contractAcceptanceDate;

    timelineEventInstances = observable.map();

    constructor(appStore) {
        this.appStore = appStore;
        this.timelineEventResource = new TimelineEventResource();
    }

    @computed
    get holidays() {
        // TODO review this, not sure about this as a computed..
        if (!this.contractAcceptanceDate) return [];

        const year = this.contractAcceptanceDate.get('year');
        const hd = new Holidays('US');
        const threeYearsAllHolidays = hd
            .getHolidays(year)
            .concat(hd.getHolidays(year + 1))
            .concat(hd.getHolidays(year + 2));
        const filteredHolidays = _.filter(threeYearsAllHolidays, x => {
            return _.values(Cons.holidays).some(y => x.name.startsWith(y));
        });
        const mappedHolidays = _.map(filteredHolidays, x => x.date);

        return mappedHolidays;
    }

    @computed
    get timelineEventInstancesCalculated() {
        const jsonInstances = this.timelineEventInstances.entries().map(([k, v]) => {
            return [k, mobx.toJS(v)];
        });

        const sortedInstances = _.sortBy(jsonInstances, x => {
            return x[1].order;
        });

        const result = sortedInstances.map(([k, v]) => {
            const a = v.exclude; // just to activate the property observe
            const contractAcceptanceDate = this.contractAcceptanceDate
                ? moment(this.contractAcceptanceDate)
                : undefined;
            return this.eventInstanceAssembler(contractAcceptanceDate, v.days, v);
        });

        return result;
    }

    getTimelineFormatted() {
        return this.timelineEventInstancesCalculated.map(timeline => {
            return {
                name: timeline.name,
                date: TimelineRenderHelper.formatTimelineInstanceDate(timeline.deadlineCalculated, timeline.exclude),
                dateShift: TimelineRenderHelper.formatTimelineInstanceDateShift(
                    timeline.weekendDateShift,
                    timeline.holidayDateShift,
                    timeline.exclude
                ),
            };
        });
    }

    getCountableDays(eventInstance) {
        return {
            holidays: eventInstance.holidaysCount ? [] : this.holidays,
            holidayFormat: 'YYYY-MM-DD HH:mm:ss', // this is the format we get from the holiday lib
            workingWeekdays: eventInstance.weekendsCount ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5],
        };
    }

    getCanLandOnWeekendDays(eventInstance) {
        return {
            workingWeekdays: eventInstance.canLandOnWeekend ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5],
        };
    }

    getCanLandOnHolidayDays(eventInstance) {
        return {
            holidays: eventInstance.canLandOnHoliday ? [] : this.holidays,
            holidayFormat: 'YYYY-MM-DD HH:mm:ss', // this is the format we get from the holiday lib
            workingWeekdays: eventInstance.canLandOnWeekend ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5],
        };
    }

    eventInstanceAssembler(date, days, eventInstance) {
        if (!date || days === '') {
            eventInstance.holidayDateShift = -1;
            eventInstance.weekendDateShift = -1;

            return eventInstance;
        }

        moment.defineLocale(
            'en-countable-days',
            Object.assign({ parentLocal: 'en' }, this.getCountableDays(eventInstance))
        );

        moment.defineLocale(
            'en-can-land-on-weekends',
            Object.assign({ parentLocal: 'en' }, this.getCanLandOnWeekendDays(eventInstance))
        );

        moment.defineLocale(
            'en-can-land-on-holidays',
            Object.assign({ parentLocal: 'en' }, this.getCanLandOnHolidayDays(eventInstance))
        );

        //Now we have our deadlineDate date based on countable days
        const deadlineDate = moment(date.format('YYYY-MM-DD'), 'YYYY-MM-DD', 'en-countable-days').businessAdd(days);

        const landOnHolidayDate = moment(deadlineDate.format('YYYY-MM-DD'), 'YYYY-MM-DD', 'en-can-land-on-holidays')
            .subtract(1, 'days')
            .nextBusinessDay();

        const start = deadlineDate.clone();
        const end = landOnHolidayDate.clone();
        const diff = end.diff(start, 'days');

        let holidayDateShift = 0,
            weekendDateShift = 0;
        for (let i = 0; i <= diff; i++) {
            let dateCopy = start.clone().add(i, 'days');
            const day = dateCopy.day();
            if (this.holidays.indexOf(dateCopy.format('YYYY-MM-DD HH:mm:ss')) >= 0) {
                if (!eventInstance.canLandOnHoliday) {
                    holidayDateShift++;
                    continue;
                }
            }
            if (day === 6 || day === 0) {
                if (!eventInstance.canLandOnWeekend) {
                    weekendDateShift++;
                }
            }
        }

        eventInstance.deadlineCalculated = landOnHolidayDate.clone();
        eventInstance.weekendDateShift = weekendDateShift;
        eventInstance.holidayDateShift = holidayDateShift;
        return eventInstance;
    }

    @action
    setContractAcceptanceDate(contractAcceptanceDate) {
        this.contractAcceptanceDate = contractAcceptanceDate;
    }

    @action
    clearContractAcceptanceDate() {
        this.contractAcceptanceDate = null;
    }

    @action
    setDays(id, days) {
        const eventInstance = this.timelineEventInstances.get(id);
        eventInstance.days = days;

        this.timelineEventInstances.set(id, eventInstance);
    }

    @action
    setExclude(id, exclude) {
        const eventInstance = this.timelineEventInstances.get(id);
        eventInstance.exclude = exclude;

        this.timelineEventInstances.set(id, eventInstance);
    }

    @action
    setExcludeFromWorkflow(id, excludeFromWorkflow) {
        const eventInstance = this.timelineEventInstances.get(id);
        eventInstance.excludeFromWorkflow = excludeFromWorkflow;

        this.timelineEventInstances.set(id, eventInstance);
    }

    @action
    setExcludeFromEmailGrid(id, excludeFromEmailGrid) {
        const eventInstance = this.timelineEventInstances.get(id);
        eventInstance.excludeFromEmailGrid = excludeFromEmailGrid;

        this.timelineEventInstances.set(id, eventInstance);
    }

    @action
    setExcludeFromCalendarReminders(id, excludeFromCalendarReminders) {
        const eventInstance = this.timelineEventInstances.get(id);
        eventInstance.excludeFromCalendarReminders = excludeFromCalendarReminders;

        this.timelineEventInstances.set(id, eventInstance);
    }

    @action
    workflowRehydrate = autorunAsync(() => {
        const calenderWorkflow = this.appStore.workflowStore.workflow.find(
            step => step.stepId === '572ace14-fb4a-4fdb-8acc-c9f4c3260c45'
        );

        if (
            calenderWorkflow &&
            calenderWorkflow.state &&
            calenderWorkflow.state.data &&
            calenderWorkflow.state.data.timeline
        ) {
            this.timelineEventInstances.clear();
            this.setContractAcceptanceDate(moment(calenderWorkflow.state.data.contractAcceptanceDate));

            calenderWorkflow.state.data.timeline.forEach(instance => {
                this.timelineEventInstances.set(
                    instance.timelineEventId,
                    Object.assign(new TimelineEventInstanceModel(), instance)
                );
            });
        }
    });

    setName(id, name) {
        const eventInstance = this.timelineEventInstances.get(id);
        eventInstance.name = name;

        this.timelineEventInstances.set(id, eventInstance);
    }

    @action
    switchPositons(oldPosition, newPosition) {
        const movingItem = this.timelineEventInstances.values().find(instance => instance.order === oldPosition);
        const max = Math.max(oldPosition, newPosition);
        const min = Math.min(oldPosition, newPosition);
        const moveBy = newPosition > oldPosition ? -1 : 1;

        const itemsToMove = this.timelineEventInstances
            .values()
            .filter(instance => instance.order >= min && instance.order <= max);
        itemsToMove.forEach(item => {
            item.order += moveBy;

            this.timelineEventInstances.set(item.timelineEventId, item);
        });

        movingItem.order = newPosition;
        this.timelineEventInstances.set(movingItem.timelineEventId, movingItem);
    }

    @action
    addTimelineEvent() {
        const customEvents = this.timelineEventInstances.values().filter(instance => instance.custom);

        if (customEvents.length < 50) {
            const event = new TimelineEventInstanceModel();
            event.timelineEventId = uuid.v4();
            event.state = this.appStore.tcFileStore.selectedFile.state
                ? this.appStore.tcFileStore.selectedFile.state.toLowerCase()
                : 'ca';
            event.exclude = false;
            event.weekendsCount = true;
            event.holidaysCount = true;
            event.canLandOnWeekend = true;
            event.canLandOnHoliday = true;
            event.order = this.timelineEventInstances.size + 1;
            event.custom = true;
            event.reminders = [3, 1, 0];
            event.recurrences = [];
            event.days = null;
            event.name = '';

            this.timelineEventInstances.set(event.timelineEventId, event);
        }
    }

    @action
    removeTimelineEvent(timelineEventId) {
        const currentOrder = this.timelineEventInstances.get(timelineEventId).order;
        this.timelineEventInstances.delete(timelineEventId);

        this.reorderFromRemove(currentOrder);
    }

    reorderFromRemove(order) {
        const itemsToMove = this.timelineEventInstances.values().filter(x => x.order >= order);

        itemsToMove.forEach(item => {
            item.order = item.order - 1;

            this.timelineEventInstances.set(item.timelineEventId, item);
        });
    }

    @action
      loadAllSteps() {

        return this.timelineEventResource
            .getAllTimelineEvents(this.appStore.authStore.getAccessToken())
            .then(events => {
                return events;
            })
            .catch(error => {
                this.isLoading = false;
                console.log(error);
            });

    }

    @action
    loadTimelineEvents() {
        this.isLoading = true;

        //Determine state that should be used.  Default is CA
        const stateFromAddress = this.appStore.tcFileStore.selectedFile.state
            ? this.appStore.tcFileStore.selectedFile.state.toLowerCase()
            : 'ca';
        const isSupportedState = _.includes(Cons.supportedStatesForCalendar, stateFromAddress);
        const stateForCalendar = isSupportedState ? stateFromAddress : 'ca';

        return this.timelineEventResource
            .getTimelineEventForState(this.appStore.authStore.getAccessToken(), stateForCalendar)
            .then(events => {
                runInAction(() => {
                    this.isLoading = false;

                    this.timelineEventInstances.clear();
                    events.forEach(event => {
                        this.timelineEventInstances.set(
                            event.timelineEventId,
                            Object.assign(new TimelineEventInstanceModel(), event)
                        );
                    });
                });
            })
            .catch(error => {
                this.isLoading = false;
                console.log(error);
            });
    }
}



// WEBPACK FOOTER //
// ./src/workflow/steps/timeline/timeline-workflow-step-store.js